/**
 * WorkerDashboardV2.js - Redesigned Worker Dashboard
 * Mobile-first job marketplace experience with ALL worker features
 * Primary purpose: Help workers find and pick up jobs quickly
 * Includes: Clock In/Out, Training, Call Outs, Incident Reports, Tips, Reviews, etc.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Menu, LogOut, Settings, User, ChevronRight, X,
  Briefcase, Home, DollarSign, Search, Calendar, Car, Power,
  Clock, GraduationCap, PhoneOff, AlertTriangle, Star, Award,
  FileText, CreditCard, Receipt, Users, MessageSquare, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { jobsAPI, workersAPI, driversAPI } from '../services/api';
import NotificationBell from '../components/NotificationBell';

// Import new worker components
import WorkerJobFeed from '../components/worker/WorkerJobFeed';
import WorkerMyJobs from '../components/worker/WorkerMyJobs';
import WorkerEarnings from '../components/worker/WorkerEarnings';
import WorkerMobileNav from '../components/worker/WorkerMobileNav';
import ServiceWorkflowRouter from '../components/worker/ServiceWorkflowRouter';
import DriverRidesView from '../components/worker/DriverRidesView';
import WorkerScheduleCalendar from '../components/worker/WorkerScheduleCalendar';
import TeamChat from '../components/worker/TeamChat';

// Import V1 components for full feature access
import CallOutTab from '../components/worker/CallOutTab';
import AvailabilityTab from '../components/worker/AvailabilityTab';
import WorkerPaymentSetup from '../components/worker/WorkerPaymentSetup';
import WorkerTaxForms from '../components/worker/WorkerTaxForms';
import IncidentReportSection from '../components/shared/IncidentReportSection';

const WorkerDashboardV2 = ({ previewMode = false, defaultTab = 'home' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
  // Preview mode sample data
  const previewUser = previewMode ? {
    id: 'preview-driver',
    full_name: 'Sample Driver',
    email: 'driver@preview.com',
    role: 'worker'
  } : user;
  
  const previewWorkerProfile = previewMode ? {
    id: 'preview-driver',
    full_name: 'Sample Driver',
    service_types: ['rides'],
    is_driver: true,
    rating: 4.9,
    total_jobs: 150,
    status: 'active'
  } : workerProfile;
  
  // Job counts for nav badges
  const [jobCounts, setJobCounts] = useState({
    jobs: 0,
    myjobs: 0
  });
  
  // Driver state
  const [isDriverOnline, setIsDriverOnline] = useState(false);
  const [driverProfile, setDriverProfile] = useState(null);
  const [togglingOnline, setTogglingOnline] = useState(false);
  
  // Workflow state
  const [activeJob, setActiveJob] = useState(null);
  const [showWorkflow, setShowWorkflow] = useState(false);
  
  // Fetch driver profile if user is a driver
  const fetchDriverProfile = useCallback(async () => {
    if (previewMode) return;
    const isDriver = user?.is_driver || workerProfile?.is_driver;
    if (!isDriver) return;
    
    try {
      const response = await driversAPI.getMyProfile();
      if (response.data && !response.data.status) {
        setDriverProfile(response.data);
        setIsDriverOnline(response.data.is_available || false);
      }
    } catch (error) {
      // Driver profile might not exist
      console.log('No driver profile found');
    }
  }, [user, workerProfile, previewMode]);
  
  // Toggle driver online/offline
  const toggleDriverOnline = async () => {
    if (togglingOnline) return;
    setTogglingOnline(true);
    
    try {
      const newStatus = !isDriverOnline;
      const response = await driversAPI.setAvailability(newStatus);
      setIsDriverOnline(newStatus);
      toast.success(response.data.message || `You are now ${newStatus ? 'online' : 'offline'}`);
    } catch (error) {
      toast.error('Failed to update availability');
    } finally {
      setTogglingOnline(false);
    }
  };
  
  // Fetch worker profile
  const fetchWorkerProfile = useCallback(async () => {
    if (previewMode) {
      setWorkerProfile(previewWorkerProfile);
      return;
    }
    if (!user) return;
    
    try {
      const response = await workersAPI.getMyProfile();
      setWorkerProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch worker profile:', error);
      // Worker profile might not exist yet
    }
  }, [user, previewMode]);
  
  // Fetch job counts
  const fetchJobCounts = useCallback(async () => {
    if (previewMode) {
      setJobCounts({ jobs: 12, myjobs: 3 });
      return;
    }
    try {
      const [availableRes, myJobsRes] = await Promise.all([
        jobsAPI.getAvailable({}),
        jobsAPI.getMyAssignments({})
      ]);
      
      const availableJobs = availableRes.data?.jobs?.length || 0;
      // Backend returns { assignments: [...], weekly_hours: {...} }
      const myJobsList = myJobsRes.data?.assignments || myJobsRes.data?.jobs || [];
      const myJobs = Array.isArray(myJobsList) 
        ? myJobsList.filter(j => !['completed', 'paid', 'cancelled'].includes(j.status?.toLowerCase())).length
        : 0;
      
      setJobCounts({
        jobs: availableJobs,
        myjobs: myJobs
      });
    } catch (error) {
      console.error('Failed to fetch job counts:', error);
    }
  }, [previewMode]);
  
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchWorkerProfile(),
        fetchJobCounts()
      ]);
      setLoading(false);
    };
    
    init();
    
    // Refresh counts periodically
    const interval = setInterval(fetchJobCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchWorkerProfile, fetchJobCounts]);
  
  // Fetch driver profile when worker profile is loaded
  useEffect(() => {
    if (workerProfile) {
      fetchDriverProfile();
    }
  }, [workerProfile, fetchDriverProfile]);
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const handleJobPickedUp = (job) => {
    toast.success(`Job picked up: ${job.service_name || job.service_type}`);
    fetchJobCounts();
  };
  
  const handleStartJob = (job) => {
    setActiveJob(job);
    setShowWorkflow(true);
  };
  
  const handleWorkflowComplete = () => {
    setShowWorkflow(false);
    setActiveJob(null);
    fetchJobCounts();
    toast.success('Job completed successfully!');
  };
  
  const handleWorkflowClose = () => {
    setShowWorkflow(false);
    setActiveJob(null);
    fetchJobCounts();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[var(--primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  
  // Display user based on mode
  const displayUser = previewMode ? previewUser : user;
  const displayProfile = previewMode ? previewWorkerProfile : workerProfile;
  
  // Home tab content - Quick overview
  const HomeContent = () => {
    const isDriver = displayUser?.is_driver || displayProfile?.is_driver;
    
    return (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[var(--primary)] to-purple-600 px-4 py-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-black/20 flex items-center justify-center">
              <span className="text-xl font-bold text-white">
                {displayUser?.full_name?.charAt(0) || 'W'}
              </span>
            </div>
            <div>
              <p className="text-white/80 text-sm">Welcome back,</p>
              <p className="text-white font-bold text-lg">
                {displayUser?.full_name?.split(' ')[0] || 'Worker'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Driver Online Toggle */}
            {isDriver && (
              <button
                onClick={toggleDriverOnline}
                disabled={togglingOnline}
                className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                  isDriverOnline 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white/20 text-white'
                }`}
                data-testid="driver-online-toggle"
              >
                <Power className={`w-4 h-4 ${togglingOnline ? 'animate-pulse' : ''}`} />
                <span className="text-xs font-medium">
                  {togglingOnline ? '...' : isDriverOnline ? 'Online' : 'Offline'}
                </span>
                {isDriverOnline && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                  </span>
                )}
              </button>
            )}
            {/* Notification Bell */}
            <NotificationBell userRole="worker" />
            <button
              onClick={() => setShowMenu(true)}
              className="p-2 bg-white/20 rounded-full"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        
        {/* Driver Status Banner */}
        {isDriver && (
          <div className={`mb-3 px-3 py-2 rounded-lg flex items-center justify-between ${
            isDriverOnline 
              ? 'bg-green-500/30 border border-green-400/50' 
              : 'bg-white/10 border border-white/20'
          }`}>
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-white" />
              <span className="text-white text-sm">
                {isDriverOnline ? 'You are accepting ride requests' : 'Go online to receive ride requests'}
              </span>
            </div>
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
            <p className="text-white/70 text-xs">Available Jobs</p>
            <p className="text-white text-2xl font-bold">{jobCounts.jobs}</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
            <p className="text-white/70 text-xs">My Active Jobs</p>
            <p className="text-white text-2xl font-bold">{jobCounts.myjobs}</p>
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="px-4 py-6">
        <h2 className="font-bold text-[var(--text-primary)] mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('jobs')}
            className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 text-left hover:border-[var(--primary)] transition-colors"
            data-testid="quick-find-jobs"
          >
            <div className="w-10 h-10 bg-[var(--primary)]/10 rounded-lg flex items-center justify-center mb-3">
              <Search className="w-5 h-5 text-[var(--primary)]" />
            </div>
            <p className="font-semibold text-[var(--text-primary)]">Find Jobs</p>
            <p className="text-sm text-[var(--text-muted)]">{jobCounts.jobs} available</p>
          </button>
          
          <button
            onClick={() => setActiveTab('myjobs')}
            className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 text-left hover:border-[var(--primary)] transition-colors"
            data-testid="quick-my-jobs"
          >
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
              <Briefcase className="w-5 h-5 text-blue-500" />
            </div>
            <p className="font-semibold text-[var(--text-primary)]">My Jobs</p>
            <p className="text-sm text-[var(--text-muted)]">{jobCounts.myjobs} active</p>
          </button>
          
          <button
            onClick={() => setActiveTab('earnings')}
            className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 text-left hover:border-[var(--primary)] transition-colors"
            data-testid="quick-earnings"
          >
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-3">
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="font-semibold text-[var(--text-primary)]">Earnings</p>
            <p className="text-sm text-[var(--text-muted)]">View payouts</p>
          </button>
          
          <button
            onClick={() => setActiveTab('profile')}
            className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 text-left hover:border-[var(--primary)] transition-colors"
            data-testid="quick-profile"
          >
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
              <User className="w-5 h-5 text-purple-500" />
            </div>
            <p className="font-semibold text-[var(--text-primary)]">Profile</p>
            <p className="text-sm text-[var(--text-muted)]">Settings</p>
          </button>
        </div>
      </div>
      
      {/* Service Types */}
      {workerProfile?.service_types && workerProfile.service_types.length > 0 && (
        <div className="px-4 pb-6">
          <h2 className="font-bold text-[var(--text-primary)] mb-3">Your Services</h2>
          <div className="flex flex-wrap gap-2">
            {workerProfile.service_types.map(service => (
              <span
                key={service}
                className="px-3 py-1.5 bg-[var(--primary)]/10 text-[var(--primary)] rounded-full text-sm font-medium"
              >
                {service.charAt(0).toUpperCase() + service.slice(1)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  };
  
  // Profile tab content - Simple settings
  const ProfileContent = () => (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4">
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Profile</h1>
      </div>
      
      <div className="p-4 space-y-4">
        {/* User Info Card */}
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
              <span className="text-2xl font-bold text-[var(--primary)]">
                {user?.full_name?.charAt(0) || 'W'}
              </span>
            </div>
            <div>
              <p className="font-bold text-lg text-[var(--text-primary)]">{user?.full_name}</p>
              <p className="text-[var(--text-muted)]">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 bg-[var(--primary)]/10 text-[var(--primary)] text-xs rounded-full font-medium">
                Worker
              </span>
            </div>
          </div>
        </div>
        
        {/* Service Types */}
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
          <h3 className="font-semibold text-[var(--text-primary)] mb-3">Service Types</h3>
          {workerProfile?.service_types && workerProfile.service_types.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {workerProfile.service_types.map(service => (
                <span
                  key={service}
                  className="px-3 py-1.5 bg-[var(--background-subtle)] text-[var(--text-secondary)] rounded-lg text-sm"
                >
                  {service.charAt(0).toUpperCase() + service.slice(1)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[var(--text-muted)] text-sm">No services configured yet</p>
          )}
        </div>
        
        {/* Quick Links */}
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl overflow-hidden">
          <button className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--background-subtle)] transition-colors">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-[var(--text-muted)]" />
              <span className="text-[var(--text-primary)]">Settings</span>
            </div>
            <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
          <div className="border-t border-[var(--border-primary)]" />
          <button 
            onClick={handleLogout}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--background-subtle)] transition-colors"
          >
            <div className="flex items-center gap-3">
              <LogOut className="w-5 h-5 text-red-500" />
              <span className="text-red-500">Log Out</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
  
  // MORE MENU - Access to ALL worker features
  const MoreMenuContent = () => {
    const MORE_MENU_ITEMS = [
      // TIME & SCHEDULE
      { section: 'Time & Schedule', items: [
        { id: 'clock', label: 'Clock In/Out', icon: Clock, color: 'green' },
        { id: 'schedule', label: 'My Schedule', icon: Calendar, color: 'blue' },
        { id: 'availability', label: 'Availability', icon: Calendar, color: 'purple' },
      ]},
      // HR & TRAINING
      { section: 'HR & Training', items: [
        { id: 'training', label: 'Training', icon: GraduationCap, color: 'blue' },
        { id: 'policies', label: 'Policies', icon: FileText, color: 'amber' },
        { id: 'callout', label: 'Call Out', icon: PhoneOff, color: 'orange' },
        { id: 'incidents', label: 'Report Issue', icon: AlertTriangle, color: 'red' },
      ]},
      // PAY & FINANCES
      { section: 'Pay & Finances', items: [
        { id: 'tips', label: 'My Tips', icon: DollarSign, color: 'green' },
        { id: 'payment-setup', label: 'Payment Setup', icon: CreditCard, color: 'blue' },
        { id: 'taxforms', label: 'Tax Forms', icon: FileText, color: 'purple' },
        { id: 'receipts', label: 'Receipt Upload', icon: Receipt, color: 'amber' },
      ]},
      // REVIEWS & RECOGNITION  
      { section: 'Reviews & Recognition', items: [
        { id: 'myreviews', label: 'My Reviews', icon: Star, color: 'yellow' },
        { id: 'badges', label: 'Badges', icon: Award, color: 'amber' },
      ]},
      // COMMUNICATION
      { section: 'Communication', items: [
        { id: 'messages', label: 'Messages', icon: MessageSquare, color: 'blue' },
        { id: 'contacts', label: 'Contacts', icon: Users, color: 'purple' },
      ]},
      // ACCOUNT
      { section: 'Account', items: [
        { id: 'profile', label: 'Profile', icon: User, color: 'gray' },
      ]},
    ];

    const getColorClass = (color) => {
      const colors = {
        green: 'bg-green-500/10 text-green-500',
        blue: 'bg-blue-500/10 text-blue-500',
        purple: 'bg-purple-500/10 text-purple-500',
        amber: 'bg-amber-500/10 text-amber-500',
        orange: 'bg-orange-500/10 text-orange-500',
        red: 'bg-red-500/10 text-red-500',
        yellow: 'bg-yellow-500/10 text-yellow-500',
        gray: 'bg-gray-500/10 text-gray-500',
      };
      return colors[color] || colors.gray;
    };

    return (
      <div className="min-h-screen bg-[var(--background)] pb-24">
        <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4">
          <h1 className="text-xl font-bold text-[var(--text-primary)]">More</h1>
        </div>
        
        <div className="p-4 space-y-4">
          {MORE_MENU_ITEMS.map(section => (
            <div key={section.section} className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-[var(--background-subtle)] border-b border-[var(--border-primary)]">
                <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">{section.section}</h3>
              </div>
              {section.items.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <React.Fragment key={item.id}>
                    {idx > 0 && <div className="border-t border-[var(--border-primary)]" />}
                    <button
                      onClick={() => setActiveTab(item.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--background-subtle)] transition-colors"
                      data-testid={`more-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getColorClass(item.color)}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-[var(--text-primary)]">{item.label}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[var(--text-muted)]" />
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          ))}
          
          {/* Logout Button */}
          <button 
            onClick={handleLogout}
            className="w-full px-4 py-3 flex items-center justify-center gap-2 bg-red-500/10 text-red-500 rounded-xl font-medium hover:bg-red-500/20 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    );
  };
  
  // Clock In/Out Content
  const ClockContent = () => {
    const [clockedIn, setClockedIn] = useState(false);
    const [clockTime, setClockTime] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    
    useEffect(() => {
      // Get current location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => setCurrentLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => toast.error('Location access needed for clock in')
        );
      }
    }, []);

    const handleClockAction = async () => {
      if (!currentLocation) {
        toast.error('Please enable location services');
        return;
      }
      try {
        const endpoint = clockedIn ? '/time-records/clock-out' : '/time-records/clock-in';
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            latitude: currentLocation.lat,
            longitude: currentLocation.lng
          })
        });
        if (response.ok) {
          setClockedIn(!clockedIn);
          setClockTime(new Date());
          toast.success(clockedIn ? 'Clocked out!' : 'Clocked in!');
        } else {
          toast.error('Failed to update time record');
        }
      } catch (error) {
        toast.error('Clock action failed');
      }
    };

    return (
      <div className="min-h-screen bg-[var(--background)] pb-24">
        <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
          <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Clock In/Out</h1>
        </div>
        
        <div className="p-4 flex flex-col items-center justify-center min-h-[60vh]">
          <div className={`w-40 h-40 rounded-full flex items-center justify-center mb-6 ${clockedIn ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
            <Clock className={`w-20 h-20 ${clockedIn ? 'text-green-500' : 'text-gray-500'}`} />
          </div>
          <p className="text-lg text-[var(--text-secondary)] mb-2">
            {clockedIn ? 'Currently Working' : 'Not Clocked In'}
          </p>
          {clockTime && (
            <p className="text-sm text-[var(--text-muted)] mb-6">
              Since {clockTime.toLocaleTimeString()}
            </p>
          )}
          <Button
            onClick={handleClockAction}
            className={`px-8 py-4 text-lg ${clockedIn ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
            data-testid={clockedIn ? 'clock-out-btn' : 'clock-in-btn'}
          >
            {clockedIn ? 'Clock Out' : 'Clock In'}
          </Button>
          {!currentLocation && (
            <p className="text-sm text-amber-500 mt-4">Enable location for clock in</p>
          )}
        </div>
      </div>
    );
  };

  // Training Content (placeholder)
  const TrainingContent = () => (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
        <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Training</h1>
      </div>
      <div className="p-4">
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
          <GraduationCap className="w-16 h-16 mx-auto mb-4 text-blue-500" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Training Materials</h3>
          <p className="text-[var(--text-muted)]">Check back for new training modules and certifications.</p>
        </div>
      </div>
    </div>
  );

  // Tips Content
  const TipsContent = () => {
    const [tips, setTips] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
      const fetchTips = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tips/my-tips`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (response.ok) {
            const data = await response.json();
            // Handle different response formats
            const tipsArray = Array.isArray(data) ? data : (data.tips ? data.tips : []);
            setTips(tipsArray);
          } else {
            setTips([]);
          }
        } catch (error) {
          console.log('Failed to fetch tips');
          setTips([]);
        } finally {
          setLoading(false);
        }
      };
      fetchTips();
    }, []);

    const totalTips = Array.isArray(tips) ? tips.reduce((sum, tip) => sum + (tip.amount || 0), 0) : 0;

    return (
      <div className="min-h-screen bg-[var(--background)] pb-24">
        <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
          <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">My Tips</h1>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Total Tips Earned</p>
            <p className="text-3xl font-bold">${totalTips.toFixed(2)}</p>
          </div>
          
          {loading ? (
            <p className="text-center text-[var(--text-muted)] py-8">Loading tips...</p>
          ) : tips.length === 0 ? (
            <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
              <Star className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
              <p className="text-[var(--text-muted)]">No tips yet. Keep up the great work!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tips.map((tip, idx) => (
                <div key={tip.id || idx} className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">${tip.amount?.toFixed(2)}</p>
                    <p className="text-sm text-[var(--text-muted)]">{tip.note || 'Great service!'}</p>
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {new Date(tip.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Reviews Content
  const ReviewsContent = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
      const fetchReviews = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/my-reviews`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (response.ok) {
            const data = await response.json();
            // Handle different response formats
            const reviewsArray = Array.isArray(data) ? data : (data.reviews ? data.reviews : []);
            setReviews(reviewsArray);
          } else {
            setReviews([]);
          }
        } catch (error) {
          console.log('Failed to fetch reviews');
          setReviews([]);
        } finally {
          setLoading(false);
        }
      };
      fetchReviews();
    }, []);

    const avgRating = Array.isArray(reviews) && reviews.length > 0 
      ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length 
      : 0;

    return (
      <div className="min-h-screen bg-[var(--background)] pb-24">
        <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
          <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">My Reviews</h1>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl p-4 text-white flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <Star className="w-8 h-8 fill-white" />
            </div>
            <div>
              <p className="text-sm opacity-80">Average Rating</p>
              <p className="text-3xl font-bold">{avgRating.toFixed(1)}</p>
              <p className="text-sm opacity-80">{reviews.length} reviews</p>
            </div>
          </div>
          
          {loading ? (
            <p className="text-center text-[var(--text-muted)] py-8">Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
              <Star className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-[var(--text-muted)]">No reviews yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map((review, idx) => (
                <div key={review.id || idx} className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(star => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                  <p className="text-[var(--text-primary)]">{review.comment || review.feedback || 'Great job!'}</p>
                  <p className="text-xs text-[var(--text-muted)] mt-2">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Badges Content
  const BadgesContent = () => (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
        <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Badges</h1>
      </div>
      <div className="p-4">
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
          <Award className="w-16 h-16 mx-auto mb-4 text-amber-500" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Earn Badges</h3>
          <p className="text-[var(--text-muted)]">Complete jobs and training to earn recognition badges.</p>
        </div>
      </div>
    </div>
  );

  // Messages placeholder
  const MessagesContent = () => (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
        <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Messages</h1>
      </div>
      <div className="p-4">
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 text-blue-500" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Messages</h3>
          <p className="text-[var(--text-muted)]">Chat with customers and support.</p>
        </div>
      </div>
    </div>
  );

  // Contacts placeholder
  const ContactsContent = () => (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
        <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Contacts</h1>
      </div>
      <div className="p-4">
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-purple-500" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Team Contacts</h3>
          <p className="text-[var(--text-muted)]">Contact your team and management.</p>
        </div>
      </div>
    </div>
  );

  // Policies placeholder
  const PoliciesContent = () => (
    <div className="min-h-screen bg-[var(--background)] pb-24">
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
        <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Policies</h1>
      </div>
      <div className="p-4">
        <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-amber-500" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Company Policies</h3>
          <p className="text-[var(--text-muted)]">Review company policies and handbooks.</p>
        </div>
      </div>
    </div>
  );
  
  // Side menu
  const SideMenu = () => (
    <AnimatePresence>
      {showMenu && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50"
            onClick={() => setShowMenu(false)}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-72 bg-[var(--background-paper)] z-50 shadow-xl"
          >
            <div className="p-4 border-b border-[var(--border-primary)]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                  <span className="text-xl font-bold text-[var(--primary)]">
                    {user?.full_name?.charAt(0) || 'W'}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-[var(--text-primary)]">{user?.full_name}</p>
                  <p className="text-sm text-[var(--text-muted)]">{user?.email}</p>
                </div>
              </div>
            </div>
            
            <div className="p-2">
              <button
                onClick={() => { setActiveTab('profile'); setShowMenu(false); }}
                className="w-full px-4 py-3 flex items-center gap-3 rounded-lg hover:bg-[var(--background-subtle)]"
              >
                <User className="w-5 h-5 text-[var(--text-muted)]" />
                <span className="text-[var(--text-primary)]">Profile</span>
              </button>
              <button
                onClick={() => { setActiveTab('earnings'); setShowMenu(false); }}
                className="w-full px-4 py-3 flex items-center gap-3 rounded-lg hover:bg-[var(--background-subtle)]"
              >
                <DollarSign className="w-5 h-5 text-[var(--text-muted)]" />
                <span className="text-[var(--text-primary)]">Earnings</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 flex items-center gap-3 rounded-lg hover:bg-[var(--background-subtle)] text-red-500"
              >
                <LogOut className="w-5 h-5" />
                <span>Log Out</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
  
  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Preview Mode Banner */}
      {previewMode && (
        <div className={`${defaultTab === 'rides' ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gradient-to-r from-green-600 to-teal-600'} text-white py-2 px-4 text-center text-sm font-medium sticky top-0 z-50 flex items-center justify-center gap-4`}>
          <span>
            <span className="mr-2">{defaultTab === 'rides' ? '🚗' : '👷'}</span>
            {defaultTab === 'rides' ? 'DRIVER' : 'WORKER'} PREVIEW MODE - This is how {defaultTab === 'rides' ? 'drivers' : 'workers'} see their dashboard
            <span className="ml-2">{defaultTab === 'rides' ? '🚗' : '👷'}</span>
          </span>
          <button
            onClick={() => window.location.href = '/owner/dashboard'}
            className="px-3 py-1 bg-white/20 rounded text-sm font-medium hover:bg-white/30 transition-colors"
          >
            ← Back to Owner Dashboard
          </button>
        </div>
      )}
      
      {/* Main Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'home' && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <HomeContent />
          </motion.div>
        )}
        
        {activeTab === 'jobs' && (
          <motion.div key="jobs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <WorkerJobFeed onJobPickedUp={handleJobPickedUp} workerServiceTypes={workerProfile?.service_types || []} />
          </motion.div>
        )}
        
        {activeTab === 'myjobs' && (
          <motion.div key="myjobs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <WorkerMyJobs onStartJob={handleStartJob} />
          </motion.div>
        )}
        
        {activeTab === 'earnings' && (
          <motion.div key="earnings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <WorkerEarnings />
          </motion.div>
        )}
        
        {activeTab === 'profile' && (
          <motion.div key="profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ProfileContent />
          </motion.div>
        )}
        
        {activeTab === 'rides' && (
          <motion.div key="rides" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DriverRidesView onRideComplete={(ride) => { toast.success('Ride completed!'); fetchJobCounts(); }} />
          </motion.div>
        )}

        {/* MORE MENU */}
        {activeTab === 'more' && (
          <motion.div key="more" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <MoreMenuContent />
          </motion.div>
        )}

        {/* CLOCK IN/OUT */}
        {activeTab === 'clock' && (
          <motion.div key="clock" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ClockContent />
          </motion.div>
        )}

        {/* SCHEDULE */}
        {activeTab === 'schedule' && (
          <motion.div key="schedule" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">My Schedule</h1>
              </div>
              <div className="p-4">
                <WorkerScheduleCalendar />
              </div>
            </div>
          </motion.div>
        )}

        {/* AVAILABILITY */}
        {activeTab === 'availability' && (
          <motion.div key="availability" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Availability</h1>
              </div>
              <div className="p-4">
                <AvailabilityTab workerId={user?.id} />
              </div>
            </div>
          </motion.div>
        )}

        {/* TRAINING */}
        {activeTab === 'training' && (
          <motion.div key="training" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TrainingContent />
          </motion.div>
        )}

        {/* POLICIES */}
        {activeTab === 'policies' && (
          <motion.div key="policies" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <PoliciesContent />
          </motion.div>
        )}

        {/* CALL OUT */}
        {activeTab === 'callout' && (
          <motion.div key="callout" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Call Out</h1>
              </div>
              <div className="p-4">
                <CallOutTab />
              </div>
            </div>
          </motion.div>
        )}

        {/* INCIDENTS */}
        {activeTab === 'incidents' && (
          <motion.div key="incidents" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Report Issue</h1>
              </div>
              <div className="p-4">
                <IncidentReportSection userRole="worker" />
              </div>
            </div>
          </motion.div>
        )}

        {/* TIPS */}
        {activeTab === 'tips' && (
          <motion.div key="tips" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TipsContent />
          </motion.div>
        )}

        {/* PAYMENT SETUP */}
        {activeTab === 'payment-setup' && (
          <motion.div key="payment-setup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Payment Setup</h1>
              </div>
              <div className="p-4">
                <WorkerPaymentSetup />
              </div>
            </div>
          </motion.div>
        )}

        {/* TAX FORMS */}
        {activeTab === 'taxforms' && (
          <motion.div key="taxforms" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Tax Forms</h1>
              </div>
              <div className="p-4">
                <WorkerTaxForms />
              </div>
            </div>
          </motion.div>
        )}

        {/* RECEIPTS */}
        {activeTab === 'receipts' && (
          <motion.div key="receipts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="min-h-screen bg-[var(--background)] pb-24">
              <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4 flex items-center gap-3">
                <button onClick={() => setActiveTab('more')} className="p-1"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Receipt Upload</h1>
              </div>
              <div className="p-4">
                <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-6 text-center">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Upload Receipts</h3>
                  <p className="text-[var(--text-muted)]">Submit expense receipts for reimbursement.</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* REVIEWS */}
        {activeTab === 'myreviews' && (
          <motion.div key="myreviews" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ReviewsContent />
          </motion.div>
        )}

        {/* BADGES */}
        {activeTab === 'badges' && (
          <motion.div key="badges" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <BadgesContent />
          </motion.div>
        )}

        {/* MESSAGES */}
        {activeTab === 'messages' && (
          <motion.div key="messages" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <MessagesContent />
          </motion.div>
        )}

        {/* CONTACTS */}
        {activeTab === 'contacts' && (
          <motion.div key="contacts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ContactsContent />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bottom Navigation */}
      <WorkerMobileNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        jobCounts={jobCounts}
        isDriver={workerProfile?.is_driver || user?.is_driver || false}
      />
      
      {/* Side Menu */}
      <SideMenu />
      
      {/* Service Workflow Modal */}
      {showWorkflow && activeJob && (
        <ServiceWorkflowRouter
          booking={activeJob}
          isOpen={showWorkflow}
          onClose={handleWorkflowClose}
          onComplete={handleWorkflowComplete}
        />
      )}
    </div>
  );
};

export default WorkerDashboardV2;
